'use client';
import React, { useRef, useEffect, useState } from 'react';
import { FocusScope } from '@react-aria/focus';
import PropTypes from 'prop-types';
import { Wrapper, StyledSlidingContainer, Overlay } from './styles.js';

export const SlidingContainer = ({
  children,
  onExit,
  active,
  bgColor = 'background.dark',
  alignment = 'right',
  direction = 'slideOut',
  maxWidth = 480,
  contentAlignment = 'center',
}) => {
  const overlayRef = useRef(null);
  const scrollYRef = useRef(0);
  const isLockedRef = useRef(false);

  const [headerHeight, setHeaderHeight] = useState(0);

  const getHeaderHeight = () => {
    try {
      // [data-nav-chrome] marks the element that's actually position:fixed/
      // sticky; <header> itself is just a static layout container, so its
      // bounds would be wrong once the page scrolls.
      const navChrome = document.querySelector('[data-nav-chrome]');
      return navChrome.getBoundingClientRect().bottom;
    } catch {
      return 0;
    }
  };

  const activeCSS = () => {
    scrollYRef.current = window.pageYOffset;
    document.body.style.top = `${-scrollYRef.current}px`;
    document.body.style.position = 'fixed';
    document.body.style.overflow = 'hidden';
    document.body.style.width = '100%';
    // Collapsing body out of flow like this clamps window.scrollY to 0 and
    // fires a scroll event as a side effect, even though the user didn't
    // scroll. This flag lets scroll-position-driven consumers (e.g.
    // useNavTopOffset) ignore that spurious event while the lock is active.
    document.body.dataset.scrollLocked = 'true';
    isLockedRef.current = true;
  };

  const cleanUpCSS = () => {
    if (!isLockedRef.current) return;
    document.body.style.removeProperty('top');
    document.body.style.removeProperty('position');
    document.body.style.removeProperty('width');
    document.body.style.removeProperty('overflow');
    // Clear the lock before restoring scroll so the native scroll event
    // this triggers is seen (not ignored) by listeners like useNavTopOffset.
    delete document.body.dataset.scrollLocked;
    window.scrollTo(0, scrollYRef.current);
    isLockedRef.current = false;
  };

  useEffect(() => {
    if (active) {
      activeCSS();
      setHeaderHeight(getHeaderHeight());
    } else {
      cleanUpCSS();
    }
    // Runs on the next `active` change AND on unmount, so navigating away
    // while the drawer is still open (e.g. clicking a link inside it) still
    // releases the scroll lock instead of leaving the next page unscrollable.
    return cleanUpCSS;
  }, [active]);

  return (
    <Wrapper
      $direction={direction}
      $headerHeight={headerHeight}
      $forward={active}
    >
      <Overlay
        $active={active}
        ref={overlayRef}
        $direction={direction}
        $headerHeight={headerHeight}
        onClick={() => onExit()}
        key={headerHeight + '_overlay_id'}
      />
      <StyledSlidingContainer
        $active={active}
        $bgColor={bgColor}
        $alignment={alignment}
        $contentAlignment={contentAlignment}
        $maxWidth={maxWidth}
        $direction={direction}
        $headerHeight={headerHeight}
        key={headerHeight + '_id'}
      >
        {active && (
          <FocusScope contain restoreFocus autoFocus>
            {children}
          </FocusScope>
        )}
      </StyledSlidingContainer>
    </Wrapper>
  );
};

SlidingContainer.displayName = 'SlidingContainer';

SlidingContainer.propTypes = {
  children: PropTypes.node,
  onExit: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  bgColor: PropTypes.string,
  alignment: PropTypes.oneOf(['left', 'right']),
  direction: PropTypes.oneOf(['slideOut', 'slideDown']),
  maxWidth: PropTypes.number,
};
